import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, Typography, Paper, Box, Button, Grid, 
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText,
  Snackbar, Alert
} from '@mui/material';
import { 
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const ScheduleXmlCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });
  const [templates, setTemplates] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState([]);
  const [lastClickedDate, setLastClickedDate] = useState(null);
  const [showTemplateSelect, setShowTemplateSelect] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', content: '', action: null });
  const { getAuthHeaders, user, hasRole, hasPermission } = useAuth();
  
  // Jogosultság ellenőrzés - sablon hozzárendeléshez kell a canAssignTemplates jogosultság
  const canAssign = hasPermission('canAssignTemplates');

  const loadData = useCallback(async () => {
    setLoading(true);
    
    // Külön try-catch a sablonok lekérdezéséhez
    try {
      console.log('Fetching templates...');
      const templatesData = await axios.get('/api/schedule-xml/templates', { headers: getAuthHeaders() });
      console.log('Templates loaded:', templatesData.data);
      
      // 🔍 DEBUG: Validate template data integrity
      templatesData.data.forEach((template, index) => {
        console.log(`🔍 Template ${index}:`, {
          id: template.id,
          idType: typeof template.id,
          name: template.name,
          nameType: typeof template.name,
          fullObject: template
        });
      });
      
      setTemplates(templatesData.data);
    } catch (err) {
      console.error('Templates load error:', err);
      console.error('Error details:', err.response?.data);
      setSnackbar({ open: true, message: 'Hiba a sablonok betöltésekor: ' + (err.response?.data?.message || err.message), severity: 'error' });
    }
    
    // Külön try-catch a naptár lekérdezéséhez
    try {
      console.log('Fetching calendar...');
      const calendarData = await axios.get(`/api/schedule-xml/calendar?month=${currentMonth}`, { headers: getAuthHeaders() });
      console.log('Calendar loaded:', calendarData.data);
      setCalendar(calendarData.data);
    } catch (err) {
      console.error('Calendar load error:', err);
      console.error('Error details:', err.response?.data);
      setSnackbar({ open: true, message: 'Hiba a naptár betöltésekor: ' + (err.response?.data?.message || err.message), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentMonth, getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getDaysInMonth = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const days = [];
    
    // Add empty cells for days before the first day of month
    const startDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday = 0
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${currentMonth}-${String(d).padStart(2, '0')}`;
      const calEntry = calendar.find(c => c.date === dateStr);
      const dayOfWeek = new Date(year, month - 1, d).getDay();
      days.push({
        day: d,
        date: dateStr,
        dayOfWeek,
        templateId: calEntry?.templateId,
        templateName: calEntry?.templateName,
        dayType: calEntry?.dayType,
        isOverride: calEntry?.isOverride,
        notes: calEntry?.notes
      });
    }
    
    return days;
  };

  const isFutureDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Éjfélre állítjuk, hogy csak a dátumot hasonlítsuk össze
    const date = new Date(dateStr);
    return date > today;
  };

  const handleDayClick = (dayInfo, event) => {
    if (!dayInfo) return;
    
    // Ellenőrizzük, hogy jövőbeli dátum-e (Admin felhasználók múltbeli dátumokra is hozzárendelhetnek)
    const canSelectPastDates = user?.permissions?.canUploadPastDates || hasRole('Admin');
    console.log('[Calendar] Day click:', dayInfo.date, 'canSelectPastDates:', canSelectPastDates, 'user.role:', user?.role, 'hasRole(Admin):', hasRole('Admin'));
    
    if (!isFutureDate(dayInfo.date) && !canSelectPastDates) {
      setSnackbar({ 
        open: true, 
        message: 'Csak jövőbeli dátumokra lehet sablont hozzárendelni!', 
        severity: 'warning' 
      });
      return;
    }
    
    if (event.shiftKey && lastClickedDate) {
      // Shift+click: select range
      const canSelectPastDates = user?.permissions?.canUploadPastDates || hasRole('Admin');
      const allDates = getDaysInMonth()
        .filter(d => d !== null && (canSelectPastDates || isFutureDate(d.date))) // Admin múltbeli dátumokat is választhat
        .map(d => d.date);
      const startIdx = allDates.indexOf(lastClickedDate);
      const endIdx = allDates.indexOf(dayInfo.date);
      const [minIdx, maxIdx] = [Math.min(startIdx, endIdx), Math.max(startIdx, endIdx)];
      const selectedRange = allDates.slice(minIdx, maxIdx + 1);
      setSelectedDates(selectedRange);
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl+click: toggle individual selection
      setSelectedDates(prev => 
        prev.includes(dayInfo.date) 
          ? prev.filter(d => d !== dayInfo.date)
          : [...prev, dayInfo.date]
      );
      setLastClickedDate(dayInfo.date);
    } else {
      // Normal click: single selection
      setSelectedDates([dayInfo.date]);
      setLastClickedDate(dayInfo.date);
    }
    
    setShowTemplateSelect(true);
  };

  const handleTemplateAssign = async (templateId) => {
    if (selectedDates.length === 0) return;
    
    console.log('🔍 DEBUG handleTemplateAssign - templateId:', templateId, typeof templateId);
    console.log('🔍 DEBUG templates array:', templates);
    
    // Csak valódi sablon ID-val hívjuk meg a funkciót, törlés nem lehetséges
    if (templateId) {
      // Sablon kiválasztása esetén azonnal végrehajtjuk
      await performTemplateAssign(templateId);
    }
  };
  
  const performTemplateAssign = async (templateId) => {
    try {
      // Debug log
      console.log(`Assigning template ${templateId} to dates:`, selectedDates);
      
      // Explicit konverzió: biztosítjuk, hogy a templateId szám típusú legyen
      const numericTemplateId = parseInt(templateId, 10);
      console.log(`🔍 Converted templateId: ${numericTemplateId}, type: ${typeof numericTemplateId}`);
      
      // Szekvenciálisan futtatjuk az API hívásokat, hogy elkerüljük a versenyhelyzetet
      for (const date of selectedDates) {
        console.log(`Processing date: ${date}`);
        
        await axios.put(`/api/schedule-xml/calendar/${date}`, {
          templateId: numericTemplateId || null,
          isOverride: true,
          notes: null
        }, { headers: getAuthHeaders() });
        
        console.log(`Successfully processed date: ${date}`);
      }
      
      // Várunk egy kicsit, hogy az adatbázis műveletek biztosan befejeződjenek
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setShowTemplateSelect(false);
      setSelectedDates([]);
      
      // Újratöltjük az adatokat és várunk, hogy befejeződjön
      await loadData();
      
      setSnackbar({ 
        open: true, 
        message: templateId ? 'Sablon sikeresen hozzárendelve!' : 'Sablon sikeresen eltávolítva!', 
        severity: 'success' 
      });
      
      // Ha megerősítő dialógusból jöttünk, bezárjuk
      setConfirmDialog({ ...confirmDialog, open: false });
    } catch (err) {
      console.error('Assign template error:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Hiba a sablon kezelésekor', severity: 'error' });
      // Hiba esetén is bezárjuk a dialógust
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  const prevMonth = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    const prev = month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
    setCurrentMonth(prev);
  };

  const nextMonth = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    const next = month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
    setCurrentMonth(next);
  };

  const days = getDaysInMonth();
  const weekDays = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Menetrend naptár
          </Typography>
          {canAssign && (
            <Button
              variant="outlined"
              component={RouterLink}
              to="/schedule-xml/templates"
              startIcon={<SettingsIcon />}
            >
              Sablonok kezelése
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button onClick={prevMonth} variant="outlined">&lt; Előző hónap</Button>
          <Typography variant="h6">
            {new Date(currentMonth + '-01').toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' })}
          </Typography>
          <Button onClick={nextMonth} variant="outlined">Következő hónap &gt;</Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Grid container spacing={0} sx={{ borderBottom: '1px solid #ddd', borderRight: '1px solid #ddd' }}>
            {weekDays.map(day => (
              <Grid item xs={12/7} key={day} sx={{ p: 1, textAlign: 'center', borderLeft: '1px solid #ddd', borderTop: '1px solid #ddd', fontWeight: 'bold' }}>
                {day}
              </Grid>
            ))}
            
            {days.map((dayInfo, idx) => (
              <Grid 
                item 
                xs={12/7} 
                key={idx} 
                sx={{ 
                  height: '100px',
                  p: 1, 
                  borderLeft: '1px solid #ddd', 
                  borderTop: '1px solid #ddd',
                  backgroundColor: dayInfo ? (
                    !isFutureDate(dayInfo.date) ? '#f5f5f5' : // Múltbeli vagy aktuális nap szürke
                    selectedDates.includes(dayInfo.date) ? '#e3f2fd' :
                    dayInfo.dayOfWeek === 0 ? '#fff8e1' :
                    dayInfo.dayOfWeek === 6 ? '#fff8e1' : 'white'
                  ) : '#f5f5f5',
                  cursor: dayInfo ? 'pointer' : 'default',
                  position: 'relative',
                  '&:hover': dayInfo ? { backgroundColor: '#f5f5f5' } : {}
                }}
                onClick={(e) => dayInfo && handleDayClick(dayInfo, e)}
              >
                {dayInfo && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {dayInfo.day}
                    </Typography>
                    {dayInfo.templateName && (
                      <Box sx={{ 
                        mt: 1, 
                        p: 0.5, 
                        borderRadius: 1, 
                        backgroundColor: dayInfo.isOverride ? '#e8f5e9' : '#e3f2fd',
                        fontSize: '0.75rem'
                      }}>
                        {dayInfo.templateName}
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Kattintson egy napra a sablon hozzárendeléséhez. Használja a Ctrl+kattintást több nap kiválasztásához, vagy a Shift+kattintást tartomány kiválasztásához.
          </Typography>
        </Box>
      </Paper>

      {/* Sablon választó dialógus */}
      <Dialog 
        open={showTemplateSelect} 
        onClose={() => { setShowTemplateSelect(false); setSelectedDates([]); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedDates.length === 1 ? selectedDates[0] : `${selectedDates.length} nap kiválasztva`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Válassz sablont a kijelölt nap(ok)hoz.
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom>Meglévő sablon választása</Typography>
            <List>
              {templates.map(template => (
                <ListItem 
                  key={template.id} 
                  button 
                  onClick={() => {
                    console.log('🔍 CLICK template object:', template);
                    console.log('🔍 CLICK template.id:', template.id, typeof template.id);
                    console.log('🔍 CLICK template.name:', template.name, typeof template.name);
                    handleTemplateAssign(template.id);
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                    }
                  }}
                >
                  <ListItemText primary={template.name} secondary={template.dayType} />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowTemplateSelect(false); setSelectedDates([]); }}>
            Bezárás
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Betöltés...</Typography>
        </Box>
      )}

      {/* Megerősítő dialógus */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            p: 1,
            minWidth: '350px',
            maxWidth: '450px'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'warning.main', 
          color: 'warning.contrastText', 
          borderRadius: '8px 8px 0 0',
          py: 1.5
        }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant="body1">{confirmDialog.content}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            sx={{ minWidth: '100px' }}
          >
            Mégse
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={confirmDialog.action}
            sx={{ minWidth: '100px' }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar értesítések */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          sx={{ width: '100%', fontSize: '1.1rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ScheduleXmlCalendar;
